import React, { useState, useRef, useEffect } from "react";
import { FaChevronDown, FaSearch } from "react-icons/fa";
import { AnimatePresence, motion } from "framer-motion";
import { format } from "date-fns";
import ReactDOM from "react-dom";

const CompactDataSliceSelector = ({
  selectedDataSlice,
  onSelect,
  vdbFilesCount,
  dataSlices,
  deasyUserConfig,
  tableHeader = false,
  createNewLabel = "All Data",
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  // Filter data slices to match the active connector
  const relevantDataSlices = dataSlices.filter((slice) => {
    const activeConnectorName = deasyUserConfig.vdbmConfig?.LastActive;

    // Try all possible ways to match the data slice to the active connector
    return (
      slice.data_connector_name === activeConnectorName ||
      slice.vector_db_config?.data_connector_name === activeConnectorName ||
      (slice.vector_db_config?.collection_name ===
        deasyUserConfig.vdbmConfig?.Configs?.[activeConnectorName]
          ?.collection &&
        slice.vector_db_config?.type ===
          deasyUserConfig.vdbmConfig?.Configs?.[activeConnectorName]?.type)
    );
  });

  const filteredDataSlices = relevantDataSlices.filter((slice) => {
    const query = searchQuery.toLowerCase();
    return (
      slice.name.toLowerCase().includes(query) ||
      slice.description?.toLowerCase().includes(query)
    );
  });

  // Effect to calculate dropdown position
  useEffect(() => {
    if (isExpanded && buttonRef.current) {
      // Add a small delay to allow DOM to fully render
      const timer = setTimeout(() => {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const desiredWidth = Math.max(350, buttonRect.width); // Use button width or min 350px
        const maxWidth = Math.min(600, viewportWidth - 32); // Max width 600px or viewport width - padding
        const finalWidth = Math.min(desiredWidth, maxWidth);
        const maxHeight = viewportHeight * 0.5; // Max height 50% of viewport
        const padding = 16; // Padding from viewport edges

        // Position directly below the button
        let top = buttonRect.bottom + 4;
        let left = buttonRect.left;
        let right = "auto"; // Default

        // Check horizontal overflow
        if (left + finalWidth > viewportWidth - padding) {
          // Try aligning right edge with button right edge
          left = "auto";
          right = viewportWidth - buttonRect.right;
          // Ensure it doesn't go off-left
          if (viewportWidth - right - finalWidth < padding) {
            right = padding; // Pin to right edge if aligning fails
          }
        }
        // Ensure left doesn't go negative if right alignment wasn't needed
        if (left !== "auto") {
          left = Math.max(padding, left);
        }
        // Ensure right doesn't go negative if left alignment wasn't needed
        if (right !== "auto") {
          right = Math.max(padding, right);
        }

        // Check vertical overflow (only check bottom for now)
        if (top + maxHeight > viewportHeight - padding) {
          // If dropdown won't fit below, try positioning above button
          if (buttonRect.top > maxHeight + padding) {
            top = buttonRect.top - maxHeight - 4;
          } else {
            // Not enough space above either, position at bottom of viewport with scroll
            top = Math.max(padding, viewportHeight - padding - maxHeight);
          }
        }

        setDropdownStyle({
          position: "fixed",
          top: `${top}px`,
          left: left === "auto" ? "auto" : `${left}px`,
          right: right === "auto" ? "auto" : `${right}px`,
          width: `${finalWidth}px`,
          maxWidth: `${maxWidth}px`,
          maxHeight: `${maxHeight}px`,
          zIndex: 9999,
        });
      }, 100); // Small delay is still needed but we can reduce it

      // Cleanup function to clear the timeout if component unmounts
      return () => clearTimeout(timer);
    }
  }, [isExpanded]); // Rerun when dropdown opens

  // Create dropdown portal component
  const DropdownPortal = () => {
    if (!isExpanded) return null;

    return ReactDOM.createPortal(
      <>
        <div
          className="fixed inset-0"
          style={{ zIndex: 9998 }}
          onClick={() => setIsExpanded(false)}
        />
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          // Apply dynamic style
          style={dropdownStyle}
          className="bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden flex flex-col"
          ref={dropdownRef}
        >
          <div className="p-3 border-b border-gray-100 text-left flex-shrink-0">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search data slices..."
                className="w-full pl-10 pr-4 py-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          </div>

          {/* Table content */}
          <div className="flex-grow overflow-y-auto shadow-inner">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data Slice Name
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Files
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Updated
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr
                  onClick={() => {
                    onSelect(null);
                    setIsExpanded(false);
                  }}
                  className={`cursor-pointer hover:bg-gray-50 transition-colors text-left duration-200 ${!selectedDataSlice ? "bg-emerald-50" : ""}`}
                >
                  <td className="px-4 py-2.5 whitespace-nowrap text-sm font-medium text-gray-900">
                    {createNewLabel}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-500">
                    No filters applied
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-500">
                    {vdbFilesCount?.total_files?.toLocaleString() || "-"}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-500">
                    -
                  </td>
                </tr>
                {filteredDataSlices.map((slice) => (
                  <tr
                    key={slice.id}
                    onClick={() => {
                      onSelect(slice);
                      setIsExpanded(false);
                    }}
                    className={`cursor-pointer text-left hover:bg-gray-50 transition-colors duration-200 ${selectedDataSlice?.id === slice.id ? "bg-emerald-50" : ""}`}
                  >
                    <td className="px-4 py-2.5 whitespace-nowrap text-sm font-medium text-gray-900">
                      {slice.name}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-500">
                      {slice.description || "-"}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-500">
                      {slice.dataPoints?.toLocaleString() || "-"}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-500">
                      {format(new Date(slice.lastUpdated), "MMM d, yyyy")}
                    </td>
                  </tr>
                ))}
                {filteredDataSlices.length === 0 && (
                  <tr>
                    <td
                      colSpan="4"
                      className="px-6 py-4 text-center text-gray-500"
                    >
                      {relevantDataSlices.length === 0
                        ? "No data slices available for current VDB configuration."
                        : "No data slices found matching your search."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </>,
      document.body,
    );
  };
  return (
    <div className="relative" style={{ zIndex: isExpanded ? 9999 : 1 }}>
      <button
        ref={buttonRef}
        onClick={() => setIsExpanded(!isExpanded)}
        className={`flex items-center w-full gap-2 px-3 py-1.5 text-xs rounded-lg transition-colors
          ${
            selectedDataSlice
              ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
              : "bg-gray-50 text-gray-600 hover:bg-gray-100"
          } border border-gray-200`}
      >
        <span className="truncate max-w-[150px]">
          {selectedDataSlice?.name || createNewLabel}
        </span>
        <FaChevronDown className="w-3 h-3 flex-shrink-0" />
      </button>

      <AnimatePresence>{isExpanded && <DropdownPortal />}</AnimatePresence>
    </div>
  );
};

export default CompactDataSliceSelector;
